import {
  convertDateStringToUSFormat,
  formatHumanReadableDate,
  formatUSPhoneNumber,
  formatUsDateToYyyyMmDd,
} from "@/src/lib/utils/date-and-phone-utils";
import { hasValue } from "@/src/lib/utils/object-utils";

export type FieldType = "text" | "tel" | "date" | "datetime";

/**
 * Formats a value for display based on its type
 * @param value - The value to format
 * @param type - The type of field ("text" | "phone" | "date")
 * @returns Formatted value for display
 */
export function getDisplayValue(value: string | null, type: FieldType): string {
  let displayValue = value || "N/A";

  if (displayValue !== "N/A") {
    switch (type) {
      case "tel":
        displayValue = formatUSPhoneNumber(displayValue) || displayValue;
        break;
      case "date":
        displayValue = convertDateStringToUSFormat(displayValue) || displayValue;
        break;
      case "datetime":
        displayValue = formatHumanReadableDate(displayValue, { includeTime: true }) || displayValue;
        break;
      default:
        // For text fields, no special formatting needed
        break;
    }
  }

  return displayValue;
}

/**
 * Normalizes values for comparison by type to avoid false conflicts
 * @param value - The value to normalize
 * @param type - The type of field ("text" | "phone" | "date")
 * @returns Normalized value for comparison
 */
export function normalizeForComparison(value: any | null, type: FieldType): string {
  if (!value) return "";

  const valueStr = String(value);

  if (!hasValue(valueStr)) return "";

  const trimmedVal = valueStr.trim();

  switch (type) {
    case "tel": {
      const [main, ext] = trimmedVal.split(/\s*(?:ext\.?|extension|x)\s*/i, 2).map((p) => p?.replace(/\D/g, "") ?? "");
      const mainNorm = main.length === 11 && main.startsWith("1") ? main.slice(1) : main;

      return (ext ? `${mainNorm}x${ext}` : mainNorm) || "N/A";
    }

    case "date":
      // Date normalization - use existing utility function
      const normalizedDate = formatUsDateToYyyyMmDd(trimmedVal);

      return normalizedDate || "N/A";

    case "datetime":
      // Datetime normalization - use existing utility function
      const normalizedDatetime = formatHumanReadableDate(trimmedVal);

      return normalizedDatetime || "N/A";

    default:
      // Text normalization - case insensitive
      return trimmedVal.toLowerCase();
  }
}

export function compareStrings(value1: string, value2: string, type: FieldType): boolean {
  return normalizeForComparison(value1, type) === normalizeForComparison(value2, type);
}

/**
 * Capitalizes the first character of a string (e.g. for enum display values).
 */
export function capitalizeFirst(s: string): string {
  if (!s || s.length === 0) return s;

  return s.charAt(0).toUpperCase() + s.slice(1);
}
